<template>
  <div>
    <CCard>
      <CCardHeader>
        <h2>
          <span>プロジェクト管理</span>
          <span class="card-header-actions">
            <router-link :to="{ name: projectRegister }">
              <button class="btn btn-primary">新規登録</button>
            </router-link>
          </span>
        </h2>
      </CCardHeader>
      <CCardBody>
        <CAlert color="info">
          プロジェクトの登録・更新・削除と、自分自身の所属プロジェクトの追加・解除ができます。
          他の社員の所属変更、通知設定、金額設定は管理権限を持つ担当者が行います。
        </CAlert>

        <CRow v-if="errors.length">
          <ul>
            <li v-for="error in errors" :key="error" class="preika-form-message">
              {{ error }}
            </li>
          </ul>
        </CRow>

        <div v-if="myProjects.length" class="mb-3">
          <h5 class="mb-2">自分の所属プロジェクト</h5>
          <CRow class="mb-3">
            <CCol
              v-for="projectItem in myProjects"
              :key="projectItem.id"
              sm="12"
              md="6"
              lg="4"
              xl="3"
            >
              <CCard
                color="info"
                class="text-white card-select"
                @click="openProject(projectItem.id)"
              >
                <CCardBody class="py-3">
                  <div class="font-weight-bold">{{ projectItem.name }}</div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </div>

        <!-- 0件は失敗ではないので、赤字の欄（errors）ではなく案内の字で、表のすぐ上に出す。 -->
        <p v-if="emptyNote" class="text-muted">{{ emptyNote }}</p>
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th class="text-center">自分の所属</th>
                <th scope="col">プロジェクト名</th>
                <th scope="col">取引先企業名</th>
                <th scope="col">先方担当者</th>
                <th scope="col">プロジェクト責任者</th>
                <th scope="col">契約形態</th>
                <th scope="col">精算時間</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="item in projects">
              <tr :key="item.id">
                <td class="text-center">
                  <label class="custom-switch mb-0">
                    <input
                      type="checkbox"
                      class="custom-switch-input"
                      :id="`self-prj-${item.id}`"
                      :checked="isAssigned(item.id)"
                      :disabled="membershipSaving || deleteLoading"
                      @change="onToggle(item, $event.target.checked, $event.target)"
                    />
                    <span class="custom-switch-indicator"></span>
                  </label>
                </td>
                <td>
                  <router-link :to="projectInfoRoute(item)">
                    {{ item.name }}
                  </router-link>
                </td>
                <td>{{ item.company_name }}</td>
                <td>{{ item.staff_name }}</td>
                <td>{{ item.private_staff_name }}</td>
                <td>{{ item.contract }}</td>
                <td>{{ item.base_time }}</td>
                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-danger"
                    :disabled="membershipSaving || deleteLoading"
                    @click="openDeleteModal(item)"
                  >
                    削除
                  </button>
                </td>
              </tr>
              <!-- 所属の切り替えを断られた理由は、切り替えた行のすぐ下に出す。 -->
              <tr v-if="rowMessages[item.id]" :key="'message-' + item.id" class="row-result">
                <td colspan="8">
                  <ActionResultMessage :text="rowMessages[item.id].text" :type="rowMessages[item.id].type" />
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>

        <ProjectDeleteModal
          v-if="showDeleteModal"
          :draggable="true"
          :resizable="true"
          :val="deleteItem"
          :loading="deleteLoading"
          :cancel-disabled="deleteLoading"
          :error="deleteError"
          @delete="deleteProject"
          @close="closeDeleteModal"
        />

        <div class="d-flex justify-content-center">
          <VPagination
            v-model="currentPage"
            :click-handler="changePage"
            :page-count="totalPages"
            :force-page="currentPage"
            :force-ellipses="true"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :prev-text="'<'"
            :next-text="'>'"
            :prev-link-class="'page-link'"
            :next-link-class="'page-link'"
            :page-link-class="'page-link'"
            :class-prefix="'pagination'"
          />
        </div>
      </CCardBody>
    </CCard>

    <div
      v-if="loading || membershipSaving || deleteLoading"
      class="loading-overlay"
      aria-live="polite"
      aria-busy="true"
    >
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { backendErrorMessage } from '@/utils/rosterActualPolicy'
import VPagination from 'vuejs-paginate'
import ConstList from './../ConstList'
import ProjectDeleteModal from './ProjectDeleteModal'
import { recordQueryRoute } from '@/common/routeRecordId'
import ActionResultMessage from '@/components/ActionResultMessage.vue'

/** PROJECT_EDIT保持者向け。旧commonのプロジェクトCRUDと本人所属を提供する。 */
export default {
  name: 'ProjectEditorList',
  components: {
    ProjectDeleteModal,
    VPagination,
    ActionResultMessage
  },
  data () {
    return {
      projects: [],
      myProjects: [],
      errors: [],
      // 0件のときの案内（失敗ではないので errors には入れない）
      emptyNote: '',
      // 所属の切り替えの結果。切り替えた行のすぐ下に出す（キーはプロジェクトのid）。
      rowMessages: {},
      // 削除を断られた理由。確認の小窓を閉じずに、小窓の中に出す。
      deleteError: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      projectRegister: ConstList.ROUTE_PROJECT_REGISTER,
      projectInfo: ConstList.ROUTE_PROJECT_INFO,
      loading: false,
      membershipSaving: false,
      deleteLoading: false,
      showDeleteModal: false,
      deleteItem: null
    }
  },
  mounted () {
    this.loadPage()
  },
  methods: {
    async loadPage () {
      this.loading = true
      this.errors = []
      try {
        const [listResponse, countResponse, assignedResponse] = await Promise.all([
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/project`, {
            params: {
              currentPage: this.currentPage,
              pageSize: this.pageSize
            }
          }),
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/projectCount`),
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/projects`)
        ])

        this.projects = Array.isArray(listResponse.data) ? listResponse.data : []
        this.myProjects = Array.isArray(assignedResponse.data) ? assignedResponse.data : []
        const totalCount = Number(countResponse.data || 0)
        this.totalPages = Math.max(1, Math.ceil(totalCount / this.pageSize))
        this.emptyNote = this.projects.length ? '' : ConstList.EMPTY
      } catch (error) {
        this.emptyNote = ''
        this.errors = ['プロジェクト情報を取得できませんでした。']
        this.reportError(error)
      } finally {
        this.loading = false
      }
    },

    isAssigned (projectId) {
      return this.myProjects.some(item => Number(item.id) === Number(projectId))
    },

    async onToggle (item, nowChecked, input) {
      const previousProjects = this.myProjects.slice()
      this.$delete(this.rowMessages, item.id)
      this.membershipSaving = true
      try {
        const endpoint = nowChecked ? '/userProject/add' : '/userProject/remove'
        await axios.post(
          `${process.env.VUE_APP_API_ORIGIN}${endpoint}`,
          { projectId: item.id },
          { headers: { 'Content-Type': 'application/json' } }
        )

        if (nowChecked) {
          if (!this.isAssigned(item.id)) {
            this.myProjects = [{ id: item.id, name: item.name }, ...this.myProjects]
          }
        } else {
          this.myProjects = this.myProjects.filter(
            project => Number(project.id) !== Number(item.id)
          )
        }

        try {
          await this.refreshAssignments()
        } catch (refreshError) {
          this.$set(this.rowMessages, item.id, {
            type: 'danger',
            text: '所属は保存されましたが、最新の状態を読み込めませんでした。画面を再読み込みしてください。'
          })
          this.reportError(refreshError)
        }
      } catch (error) {
        this.myProjects = previousProjects
        // 所属は変わっていないので、スイッチも押す前へ戻す（所属の一覧は同じ値のままなので、
        // 待っていても描き直されず、押した側に残って見える）。
        if (input) {
          input.checked = !nowChecked
        }
        // 断られた理由が分かる文（打刻場所が指定されているプロジェクト、など）が返ってきたら、それを出す。
        // 理由の無い失敗は、今までの文のまま。
        this.$set(this.rowMessages, item.id, {
          type: 'danger',
          text: backendErrorMessage(error, '所属プロジェクトを更新できませんでした。画面を再読み込みしてください。')
        })
        this.reportError(error)
      } finally {
        this.membershipSaving = false
      }
    },

    async refreshAssignments () {
      const response = await axios.get(
        `${process.env.VUE_APP_API_ORIGIN}/projects`
      )
      this.myProjects = Array.isArray(response.data) ? response.data : []
    },

    openDeleteModal (item) {
      this.deleteError = ''
      this.deleteItem = item
      this.showDeleteModal = true
    },

    closeDeleteModal () {
      /*
       * 送っているあいだは閉じない（2026-09-26 監査）。閉じると、あとから届いた
       * 理由を出す場所がなくなる。消せたときは送っている最中に閉じるので、
       * そちらは hideDeleteModal を直に呼ぶ。
       */
      if (this.deleteLoading) return
      this.hideDeleteModal()
    },

    hideDeleteModal () {
      this.showDeleteModal = false
      this.deleteItem = null
      this.deleteError = ''
    },

    async deleteProject () {
      if (!this.deleteItem || !this.deleteItem.id) return

      const target = this.deleteItem
      this.deleteLoading = true
      this.deleteError = ''
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_ORIGIN}/projectDelete`,
          { id: target.id },
          { headers: { 'Content-Type': 'application/json' } }
        )
        const affiliationCount = Number(response.data || 0)
        if (affiliationCount > 0) {
          // 小窓は開いたまま、理由は小窓の中に出す（一覧の上の文は小窓の暗幕の下）。
          this.deleteError =
            `プロジェクトを設定中の方が${affiliationCount}人います。所属を解除してから削除してください。`
          return
        }

        this.projects = this.projects.filter(
          project => Number(project.id) !== Number(target.id)
        )
        this.myProjects = this.myProjects.filter(
          project => Number(project.id) !== Number(target.id)
        )
        this.hideDeleteModal()
        alert(ConstList.DELETE_MESSAGE)

        try {
          const countResponse = await axios.get(
            `${process.env.VUE_APP_API_ORIGIN}/projectCount`
          )
          const totalCount = Number(countResponse.data || 0)
          this.totalPages = Math.max(1, Math.ceil(totalCount / this.pageSize))
          if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages
            await this.loadPage()
          }
        } catch (refreshError) {
          this.errors = [
            'プロジェクトは削除されましたが、一覧を更新できませんでした。画面を再読み込みしてください。'
          ]
          this.reportError(refreshError)
        }
      } catch (error) {
        this.deleteError = 'プロジェクトを削除できませんでした。画面を再読み込みしてください。'
        this.reportError(error)
      } finally {
        this.deleteLoading = false
      }
    },

    projectInfoRoute (item) {
      return {
        ...recordQueryRoute(this.projectInfo, item.id),
        params: { value: item }
      }
    },

    openProject (id) {
      const row = this.projects.find(item => Number(item.id) === Number(id))
      if (row) {
        this.$router.push(this.projectInfoRoute(row)).catch(() => {})
        return
      }
      this.$router.push(recordQueryRoute(this.projectInfo, id)).catch(() => {})
    },

    changePage (page) {
      this.currentPage = page
      this.loadPage()
    },

    reportError (error) {
      axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
          { error: String(error) },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
th,
td {
  white-space: nowrap;
}

td {
  vertical-align: middle;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
